import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  DASHBOARD_WIDGETS,
  DEFAULT_WIDGET_IDS,
  PINNED_WIDGET_IDS,
  WIDGET_MAP,
} from '../components/Dashboard/dashboardWidgetRegistry';

interface StoredConfig {
  version: number;
  widgetIds: string[];
  updatedAt: string;
}

const STORAGE_VERSION = 1;

function storageKey(userId: string) {
  return `dashboard-config-${userId}`;
}

function readConfig(userId: string): StoredConfig | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version === STORAGE_VERSION && Array.isArray(parsed.widgetIds)) {
      return parsed as StoredConfig;
    }
    return null;
  } catch {
    return null;
  }
}

function writeConfig(userId: string, widgetIds: string[]) {
  const data: StoredConfig = {
    version: STORAGE_VERSION,
    widgetIds,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(storageKey(userId), JSON.stringify(data));
}

function ensurePinned(ids: string[]): string[] {
  const result = [...ids];
  for (const pin of PINNED_WIDGET_IDS) {
    if (WIDGET_MAP.has(pin) && !result.includes(pin)) {
      result.unshift(pin);
    }
  }
  return result;
}

const allWidgetIdSet = new Set(DASHBOARD_WIDGETS.map(w => w.id));

export function useDashboardConfig(userId: string | undefined) {
  const [widgetIds, setWidgetIds] = useState<string[]>(() => {
    if (!userId) return DEFAULT_WIDGET_IDS;
    const stored = readConfig(userId);
    if (!stored) return DEFAULT_WIDGET_IDS;
    const cleaned = stored.widgetIds.filter(id => allWidgetIdSet.has(id));
    return ensurePinned(cleaned);
  });

  useEffect(() => {
    if (!userId) {
      setWidgetIds(DEFAULT_WIDGET_IDS);
      return;
    }
    const stored = readConfig(userId);
    if (!stored) {
      setWidgetIds(DEFAULT_WIDGET_IDS);
      return;
    }
    const cleaned = stored.widgetIds.filter(id => allWidgetIdSet.has(id));
    setWidgetIds(ensurePinned(cleaned));
  }, [userId]);

  const availableIds = useMemo(() => {
    const visibleSet = new Set(widgetIds);
    return DASHBOARD_WIDGETS
      .filter(w => !visibleSet.has(w.id))
      .map(w => w.id);
  }, [widgetIds]);

  const updateConfig = useCallback((newWidgetIds: string[]) => {
    const ids = ensurePinned(newWidgetIds);
    setWidgetIds(ids);
    if (userId) writeConfig(userId, ids);
  }, [userId]);

  const resetToDefault = useCallback(() => {
    setWidgetIds(DEFAULT_WIDGET_IDS);
    if (userId) writeConfig(userId, DEFAULT_WIDGET_IDS);
  }, [userId]);

  const isPinned = useCallback((id: string) => PINNED_WIDGET_IDS.includes(id), []);

  return {
    widgetIds,
    availableIds,
    updateConfig,
    resetToDefault,
    isPinned,
  };
}
